import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Copy, RefreshCw, Link2, Mail, ShieldCheck } from "lucide-react";

export default function InvitationPanel() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotating, setRotating] = useState(false);

  const registerUrl = code
    ? `${window.location.origin}/inscription?code=${encodeURIComponent(code)}`
    : "";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/settings/invitation-code");
        setCode(data.code);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copié`);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const rotate = async () => {
    setRotating(true);
    try {
      const { data } = await http.post("/settings/invitation-code/rotate");
      setCode(data.code);
      toast.success("Nouveau code généré. L'ancien lien ne fonctionne plus.");
      setRotateOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRotating(false);
    }
  };

  const emailTemplate = `Bonjour,

Notre nouvel outil de suivi Immo Varois est en ligne. Chacun peut créer son espace en 30 secondes en cliquant sur le lien ci-dessous :

${registerUrl}

Le code d'invitation est pré-rempli. Il vous suffit de renseigner votre nom, votre email et un mot de passe.

À très vite,
La direction`;

  return (
    <Card className="p-6 mb-6 border-primary/20 bg-primary/5" data-testid="invitation-panel">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-10 w-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
          <Link2 className="h-5 w-5" />
        </div>
        <div>
          <p className="label-eyebrow mb-1">Inscription en libre-service</p>
          <h2 className="font-display font-bold text-2xl text-slate-900">
            Faites-vous rejoindre par votre équipe
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Partagez ce lien par email ou SMS. Chaque négociateur créera son propre compte en 30 secondes.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2 space-y-1.5">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Lien d'invitation</div>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={registerUrl}
                  className="h-11 text-sm bg-white font-mono"
                  data-testid="invitation-link"
                  onFocus={(e) => e.target.select()}
                />
                <Button variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={() => copy(registerUrl, "Lien")} data-testid="copy-link">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Code seul</div>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={code}
                  className="h-11 text-base bg-white font-mono tracking-wider"
                  data-testid="invitation-code"
                  onFocus={(e) => e.target.select()}
                />
                <Button variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={() => copy(code, "Code")} data-testid="copy-code">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-2">
            <Button
              onClick={() => copy(emailTemplate, "Modèle d'email")}
              variant="outline"
              className="gap-2"
              data-testid="copy-email-template"
            >
              <Mail className="h-4 w-4" /> Copier le message pour l'email groupé
            </Button>
            <Button
              variant="ghost"
              onClick={() => setRotateOpen(true)}
              className="gap-2 text-slate-600"
              data-testid="rotate-code"
            >
              <RefreshCw className="h-4 w-4" /> Renouveler le code
            </Button>
            <Badge variant="secondary" className="ml-auto gap-1.5">
              <ShieldCheck className="h-3 w-3" /> Sans ce code, personne ne peut s'inscrire
            </Badge>
          </div>
        </div>
      )}

      <AlertDialog open={rotateOpen} onOpenChange={setRotateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Renouveler le code d'invitation ?</AlertDialogTitle>
            <AlertDialogDescription>
              Un nouveau code sera généré. L'ancien lien cessera immédiatement de fonctionner — utilisez cette option si le code a fuité ou si vous voulez bloquer de nouvelles inscriptions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={rotate} disabled={rotating} data-testid="confirm-rotate">
              {rotating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Générer un nouveau code
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
